import {
  Box,
  Button,
  Container,
  Divider,
  Group,
  NumberInput,
  SimpleGrid,
  Text,
} from "@mantine/core";
import React, { useEffect, useState } from "react";
import BenchSettingsBreadcrumbs from "../UIComponent/Components/BenchSettingsBreadcrumbs";
import BenchHeading from "../pages/UI/BenchHeading";
import BenchTopHeader from "../UIComponent/Components/BenchTopHeader";
import { IconSettings, IconListDetails } from "@tabler/icons-react";
import BenchCard from "../UIComponent/Components/BenchCard";
import { toast } from "react-toastify";
import { doc, getDoc, serverTimestamp, setDoc } from "firebase/firestore";
import { useAppSelector } from "../../hooks/hooks";
import { db } from "../../firebase/FirebaseCredential";
import { RootState } from "../../store/store";
import BenchIconBackground from "../UIComponent/BenchIconBackground";

const EnquirySettings = () => {
  const [trialSessionCount, setTrialSessionCount] = useState<number>(1);
  const [fetcheddata, setFetchedData] = useState<boolean>(false);
  const companyId = useAppSelector(
    (state: RootState) => state.session?.company_id || state.auth?.companyId,
  );

  const fetchEnquirySettingsData = async () => {
    if (!companyId) return;

    const enquirysettingsdoc = doc(
      db,
      "companyprofile",
      companyId,
      "companysettings",
      "enquirysettings",
    );
    const enquirysettingsdata = await getDoc(enquirysettingsdoc);
    if (enquirysettingsdata.exists()) {
      const data = enquirysettingsdata.data();
      if (data.trial_session_count !== undefined) {
        setTrialSessionCount(data.trial_session_count);
      }
      setFetchedData(true);
    }
  };

  useEffect(() => {
    fetchEnquirySettingsData();
  }, [companyId]);

  const handleSaveSetting = async () => {
    if (!companyId) {
      toast.error("Company ID is missing. Please try refreshing the page.");
      return;
    }

    if (!trialSessionCount) {
      toast.error("Enter all the fields");
      return;
    }

    const enquirysettingsdata = {
      trial_session_count: trialSessionCount,
      updated_at: serverTimestamp(),
    };

    const updateEnquirySettings = doc(
      db,
      "companyprofile",
      companyId,
      "companysettings",
      "enquirysettings",
    );
    await setDoc(updateEnquirySettings, enquirysettingsdata, { merge: true });

    toast.success("Enquiry settings saved successfully");
    setFetchedData(true);
  };

  const breadcrumbs = [
    {
      label: "Enquiry Settings",
    },
  ];

  return (
    <Container size="lg">
      <Box
        sx={(theme) => ({
          position: "sticky",
          top: 110, // Avoid overlapping the fixed navbar
          zIndex: 100,
          backgroundColor: "#FAFAFA",
          paddingTop: theme.spacing.sm,
          marginTop: `-${theme.spacing.sm}`,
        })}
      >
        <SimpleGrid cols={2}>
          <BenchSettingsBreadcrumbs links={breadcrumbs} />
          <Group position="right">
            {!fetcheddata && (
              <Button onClick={() => handleSaveSetting()}>Save</Button>
            )}
            {fetcheddata && (
              <Button onClick={() => setFetchedData(false)}>Edit</Button>
            )}
          </Group>
        </SimpleGrid>
        <Divider my="md" />
      </Box>
      <BenchTopHeader
        icon={<IconSettings />}
        title="Enquiry Settings"
        description="Setup the enquiry configuration, settings and trial preferences for your business."
      />

      {!fetcheddata ? (
        <BenchCard>
          <SimpleGrid cols={2}>
            <BenchHeading title="Trial Settings" order={4} fw={600} fz="h1" />
          </SimpleGrid>
          <SimpleGrid cols={2} mt="md" p="xl">
            <NumberInput
              label="Maximum Trial Sessions Allowed"
              description="Maximum number of free trials a single person can book across all branches."
              placeholder="Enter the number of trials allowed"
              required
              value={trialSessionCount}
              onChange={(value) => setTrialSessionCount(value as number)}
              min={1}
              max={10}
            />
          </SimpleGrid>
        </BenchCard>
      ) : (
        <>
          <BenchCard>
            <BenchHeading
              title="Enquiry Configuration"
              order={4}
              fw={600}
              fz="h1"
            />

            <SimpleGrid
              cols={2} // Responsive: 1 column on mobile, 2 on desktop
              spacing="lg"
              mt="md"
              p="xl"
            >
              {/* Card 1: Trial Limit Metric */}
              <BenchCard
                style={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                }}
              >
                <Group align="center">
                  <Group>
                    <BenchIconBackground icon={<IconListDetails />} size={30} />
                    <Text
                      size="sm"
                      c="dimmed"
                      fw={600}
                      lts="0.5px"
                      tt="uppercase"
                    >
                      Max Free Trials
                    </Text>
                  </Group>
                </Group>

                <div>
                  <Group align="baseline">
                    <Text
                      style={{ fontSize: "2.75rem", lineHeight: 1 }}
                      fw={700}
                    >
                      {trialSessionCount || "0"}
                    </Text>
                    <Text size="sm" c="dimmed" fw={500} tt="capitalize">
                      Sessions
                    </Text>
                  </Group>
                </div>
              </BenchCard>
            </SimpleGrid>
          </BenchCard>
        </>
      )}
    </Container>
  );
};

export default EnquirySettings;
